import React, { useEffect } from 'react';

const ImageModal = ({
  isOpen,
  onClose,
  imageSrc,
  altText,
  caption,
  pageNumber,
  docLink,
  docName,
  imageMatchedTerms = [],
  dynamicHighlights,
  isLoadingDynamicHighlights,
  dynamicHighlightsError,
  // New navigation props
  currentImageIndex,
  totalImages,
  onPreviousImage,
  onNextImage,
  hasMultipleImages
}) => {
  // Effect to control body overflow when modal is open/closed
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'; // Prevent scrolling on body when modal is open
    } else {
      document.body.style.overflow = 'auto'; // Restore scrolling when modal is closed
    }
    // Cleanup function to ensure scrolling is re-enabled if component unmounts while modal is open
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]); // Re-run effect when isOpen changes

  // Keyboard navigation and close (Escape key)
  useEffect(() => {
    if (!isOpen) return; // Only attach listener if modal is open

    const handleKeyPress = (e) => {
      // Handle previous image navigation
      if (e.key === 'ArrowLeft' && onPreviousImage && hasMultipleImages) {
        e.preventDefault(); // Prevent default browser scroll behavior
        onPreviousImage();
      }
      // Handle next image navigation
      else if (e.key === 'ArrowRight' && onNextImage && hasMultipleImages) {
        e.preventDefault(); // Prevent default browser scroll behavior
        onNextImage();
      }
      // Handle Escape key to close the modal
      else if (e.key === 'Escape' && onClose) {
        e.preventDefault(); // Prevent default browser behavior (e.g., closing full-screen mode)
        onClose();
      }
    };

    // Add event listener for keydown events
    document.addEventListener('keydown', handleKeyPress);

    // Cleanup function to remove event listener when component unmounts or modal closes
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, hasMultipleImages, onPreviousImage, onNextImage, onClose]); // Re-run effect if these dependencies change

  if (!isOpen) return null; // If modal is not open, render nothing

  return (
    // Overlay for the modal, clicking outside the content closes the modal
    <div className="image-modal-overlay" onClick={onClose}>
      {/* Modal content area, prevents click propagation from overlay to content */}
      <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Container for the main image */}
        <div className="image-modal-main-image-container">
          {imageSrc ? (
            <img src={imageSrc} alt={altText} className="image-modal-fullsize" />
          ) : (
            <div className="image-modal-placeholder">Image not available</div> // Placeholder if no image source
          )}
        </div>

        {/* Details panel for image information and highlights */}
        <div className="image-modal-details-panel">
          {/* Image counter and navigation buttons, only shown if multiple images exist */}
          {hasMultipleImages && (
            <div className="image-modal-header-controls">
              {/* Image counter display */}
              <div className="image-modal-counter">
                <span>Image {currentImageIndex + 1} of {totalImages}</span>
              </div>

              {/* Group for navigation buttons */}
              <div className="image-modal-nav-buttons-group">
                <button
                  className="image-modal-nav-button image-modal-nav-prev"
                  onClick={onPreviousImage}
                  disabled={currentImageIndex === 0} // Disable if on the first image
                  title="Previous image (←)"
                >
                  <i className='bx bx-chevron-left'></i> {/* Left arrow icon */}
                </button>
                <button
                  className="image-modal-nav-button image-modal-nav-next"
                  onClick={onNextImage}
                  disabled={currentImageIndex === totalImages - 1} // Disable if on the last image
                  title="Next image (→)"
                >
                  <i className='bx bx-chevron-right'></i> {/* Right arrow icon */}
                </button>
              </div>
            </div>
          )}

          {/* Document Information Section */}
          {(docName || docLink || pageNumber !== null) && (
            <div className="image-modal-doc-info-group">
              <h4>Document Information:</h4>
              {docName && (
                <p className="image-modal-doc-name">{docName}</p>
              )}
              {docLink && (
                <p className="image-modal-doc-link-item">
                  <a
                    href={pageNumber !== null ? `${docLink}#page=${pageNumber}` : docLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="document-link"
                  >
                    <i className='bx bx-file-blank'></i>
                    {pageNumber !== null ? "View Page in Document" : "View Document"}
                  </a>
                </p>
              )}
              {pageNumber !== null && (
                <p className="image-modal-page-number-item">
                  <strong>Image is on page:</strong> {pageNumber}
                </p>
              )}
            </div>
          )}

          {/* Initial context/caption display */}
          {caption && (
            <div className="image-modal-highlight">
              <h4>Initial Context:</h4>
              <p>{caption}</p>
            </div>
          )}

          {/* Image matched terms (if any) */}
          {imageMatchedTerms && imageMatchedTerms.length > 0 && (
            <div className="image-modal-matched-terms">
              <h4>Image Matched Terms:</h4>
              <div className="image-modal-matched-terms-list">
                {imageMatchedTerms.map(([term, freq]) => (
                  <div key={term} className="image-modal-matched-term-item">{term}: {freq}</div>
                ))}
              </div>
            </div>
          )}

          {/* Dynamic Highlights Section */}
          <div className="image-modal-doc-info-group">
            <h4>Image-Specific Highlights:</h4>
            {isLoadingDynamicHighlights && (
              <div className="image-modal-loading"><i className='bx bx-loader-alt bx-spin'></i> Loading image highlights...</div>
            )}
            {dynamicHighlightsError && (
              <div className="image-modal-error"><i className='bx bx-error-circle'></i> {dynamicHighlightsError}</div>
            )}
            {!isLoadingDynamicHighlights && !dynamicHighlightsError && dynamicHighlights.length === 0 && (
              <div className="no-highlights">No specific highlights found for this image based on your query.</div>
            )}
            {!isLoadingDynamicHighlights && !dynamicHighlightsError && dynamicHighlights.length > 0 && (
              <div className="highlights-list image-modal-dynamic-highlights-list">
                {dynamicHighlights.map((highlight, idx) => (
                  <div className="highlight-item" key={`modal-img-hl-${idx}`} dangerouslySetInnerHTML={{ __html: highlight }}></div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Close button for the modal */}
        <button className="image-modal-close-button image-modal-close-button-absolute" onClick={onClose}>
          <i className='bx bx-x'></i> Close
        </button>
      </div>
    </div>
  );
};

export default ImageModal;